import { createAsyncThunk } from '@reduxjs/toolkit';
import { authService, accountService } from './services';
import { decodeJwt, isTokenExpired } from '../../utils/jwt';
import { checkAndClearOldAuth, clearAuthData } from '../../utils/clearAuth';
// ===== LOGIN THUNK =====
export const loginThunk = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
    try {
        console.log('🔐 Attempting login for:', credentials.email);
        const response = await authService.login(credentials);
        // Decode JWT payload
        const jwtPayload = decodeJwt(response.tokens.access_token);
        if (!jwtPayload) {
            throw new Error('Invalid JWT token received');
        }
        console.log('✅ Login successful via API');
        return {
            user: response.user,
            tokens: response.tokens,
            jwtPayload,
        };
    }
    catch (error) {
        console.error('❌ Login error:', error);
        const message = error instanceof Error ? error.message : 'Login failed';
        return rejectWithValue(message);
    }
});
// ===== REGISTER THUNK =====
export const registerThunk = createAsyncThunk('auth/register', async (userData, { rejectWithValue }) => {
    try {
        console.log('📝 Attempting registration for:', userData.email);
        const response = await authService.register(userData);
        console.log('✅ Registration successful via API');
        return {
            user: response.user,
            tokens: response.tokens,
        };
    }
    catch (error) {
        console.error('❌ Registration error:', error);
        const message = error instanceof Error ? error.message : 'Registration failed';
        return rejectWithValue(message);
    }
});
// ===== REFRESH TOKEN THUNK =====
export const refreshTokenThunk = createAsyncThunk('auth/refreshToken', async (_, { rejectWithValue }) => {
    try {
        const savedRefreshToken = localStorage.getItem('refresh_token');
        if (!savedRefreshToken) {
            throw new Error('No refresh token available');
        }
        console.log('🔄 Refreshing access token...');
        const response = await authService.refreshToken(savedRefreshToken);
        // Decode new JWT payload
        const jwtPayload = decodeJwt(response.access_token);
        if (!jwtPayload) {
            throw new Error('Invalid JWT token received');
        }
        console.log('✅ Token refreshed successfully');
        return {
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            jwtPayload,
        };
    }
    catch (error) {
        console.error('❌ Token refresh failed:', error);
        const message = error instanceof Error ? error.message : 'Token refresh failed';
        return rejectWithValue(message);
    }
});
// ===== LOGOUT THUNK =====
export const logoutThunk = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
    try {
        console.log('🚪 Logging out...');
        // Try to logout from API
        await authService.logout();
        console.log('✅ API logout successful');
        console.log('✅ Logout completed');
        return true;
    }
    catch (error) {
        console.warn('⚠️ Logout request failed:', error);
        // Always succeed logout to clear local state
        return true;
    }
});
// ===== LOGOUT ALL THUNK =====
export const logoutAllThunk = createAsyncThunk('auth/logoutAll', async (_, { rejectWithValue }) => {
    try {
        console.log('🚪 Logging out from all devices...');
        // Try to logout from all devices via API
        await authService.logoutAll();
        console.log('✅ API logout all successful');
        console.log('✅ Logout all completed');
        return true;
    }
    catch (error) {
        console.warn('⚠️ Logout all request failed:', error);
        // Always succeed logout to clear local state
        return true;
    }
});
// ===== CHECK AUTH THUNK =====
export const checkAuthThunk = createAsyncThunk('auth/checkAuth', async (_, { rejectWithValue }) => {
    try {
        console.log('🔍 Checking authentication...');
        // Check and clear old auth data first
        const wasCleared = checkAndClearOldAuth();
        if (wasCleared) {
            console.log('🧹 Old auth data cleared');
            return null;
        }
        // Get saved data
        const savedUser = localStorage.getItem('user');
        const savedToken = localStorage.getItem('auth_token');
        const savedRefreshToken = localStorage.getItem('refresh_token');
        if (!savedUser || !savedToken) {
            console.log('❌ No saved auth data found');
            return null;
        }
        // Check if token is expired
        if (isTokenExpired(savedToken)) {
            console.log('🔄 Token expired, attempting refresh...');
            if (savedRefreshToken) {
                try {
                    const refreshResult = await authService.refreshToken(savedRefreshToken);
                    console.log('✅ Token refreshed successfully');
                    const jwtPayload = decodeJwt(refreshResult.access_token);
                    return {
                        user: refreshResult.user || JSON.parse(savedUser),
                        tokens: {
                            access_token: refreshResult.access_token,
                            refresh_token: refreshResult.refresh_token || savedRefreshToken,
                        },
                        jwtPayload,
                    };
                }
                catch (refreshError) {
                    console.log('❌ Token refresh failed:', refreshError);
                    clearAuthData();
                    return null;
                }
            }
            else {
                console.log('❌ No refresh token available');
                clearAuthData();
                return null;
            }
        }
        // Validate token structure
        const jwtPayload = decodeJwt(savedToken);
        if (!jwtPayload) {
            console.log('❌ Invalid JWT token');
            throw new Error('Invalid JWT token');
        }
        // Parse user data
        const userData = JSON.parse(savedUser);
        // Validate token with backend
        try {
            const profileResponse = await authService.getProfile();
            console.log('✅ Token validated with backend');
            // Use profile response if available, otherwise use saved data
            const finalUserData = profileResponse || userData;
            console.log('✅ Auth check successful');
            return {
                user: finalUserData,
                tokens: {
                    access_token: savedToken,
                    refresh_token: savedRefreshToken || '',
                },
                jwtPayload,
            };
        }
        catch (error) {
            console.log('❌ Token invalid on backend, attempting refresh...');
            if (savedRefreshToken) {
                try {
                    const refreshResult = await authService.refreshToken(savedRefreshToken);
                    console.log('✅ Token refreshed after backend validation failure');
                    const newJwtPayload = decodeJwt(refreshResult.access_token);
                    return {
                        user: refreshResult.user || userData,
                        tokens: {
                            access_token: refreshResult.access_token,
                            refresh_token: refreshResult.refresh_token || savedRefreshToken,
                        },
                        jwtPayload: newJwtPayload,
                    };
                }
                catch (refreshError) {
                    console.log('❌ Token refresh failed after backend validation:', refreshError);
                    clearAuthData();
                    return null;
                }
            }
            else {
                console.log('❌ No refresh token for retry after backend validation');
                clearAuthData();
                return null;
            }
        }
    }
    catch (error) {
        console.error('❌ Auth check failed:', error);
        return null;
    }
});
// ===== CREATE ACCOUNT THUNK =====
export const createAccountThunk = createAsyncThunk('auth/createAccount', async (accountData, { rejectWithValue }) => {
    try {
        console.log('🏢 Creating account:', accountData.name);
        const response = await accountService.createAccount(accountData);
        console.log('✅ Account created successfully');
        return {
            account: response.account,
            owner: response.owner,
            tokens: response.tokens,
        };
    }
    catch (error) {
        console.error('❌ Account creation error:', error);
        const message = error instanceof Error ? error.message : 'Account creation failed';
        return rejectWithValue(message);
    }
});
// ===== UPDATE PROFILE THUNK =====
export const updateProfileThunk = createAsyncThunk('auth/updateProfile', async (profileData, { rejectWithValue }) => {
    try {
        console.log('👤 Updating profile...');
        const updatedUser = await authService.updateProfile(profileData);
        console.log('✅ Profile updated successfully');
        return updatedUser;
    }
    catch (error) {
        console.error('❌ Profile update error:', error);
        const message = error instanceof Error ? error.message : 'Profile update failed';
        return rejectWithValue(message);
    }
});
// ===== CHANGE PASSWORD THUNK =====
export const changePasswordThunk = createAsyncThunk('auth/changePassword', async (passwordData, { rejectWithValue }) => {
    try {
        console.log('🔒 Changing password...');
        const result = await authService.changePassword(passwordData);
        console.log('✅ Password changed successfully');
        return result;
    }
    catch (error) {
        console.error('❌ Password change error:', error);
        const message = error instanceof Error ? error.message : 'Password change failed';
        return rejectWithValue(message);
    }
});
// ===== CHECK API STATUS THUNK =====
export const checkApiStatusThunk = createAsyncThunk('auth/checkApiStatus', async (_, { rejectWithValue }) => {
    try {
        console.log('🔍 Checking API status...');
        const isConnected = await authService.healthCheck();
        console.log(`✅ API status: ${isConnected ? 'connected' : 'mock'}`);
        return isConnected;
    }
    catch (error) {
        console.warn('⚠️ API health check failed, using mock data:', error);
        return false;
    }
});
